import { useTranslation } from 'react-i18next'
import { formatDager } from './api'
import type { StaatidAlderBucket } from './api'

interface Props {
  rows: StaatidAlderBucket[]
  total: number
  utenAlder: number
  loading?: boolean
  /** What the breakdown is scoped to, e.g. "Toyota" or the dealer's name. */
  scope?: string
}

/**
 * Ståtid per bilalder-interval (#490 AC4/AC7) — the same shape as the
 * antall_transfers breakdown on Oversikt: one row per bucket with its own
 * count, median and mean, plus a bar showing the median relative to the
 * slowest bucket, so "old cars sit longer" is visible at a glance.
 *
 * Empty buckets are rendered, not skipped: a hole in the age distribution is
 * itself information about what the dealer buys.
 */
export default function AlderFordeling({ rows, total, utenAlder, loading, scope }: Props) {
  const { t } = useTranslation('staatid')
  if (loading) {
    return (
      <div className="card p-4">
        <div data-testid="staatid-alder-skeleton" className="animate-pulse h-48 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  const maxMedian = Math.max(...rows.map(r => r.median_dager ?? 0), 1)

  return (
    <div className="card overflow-hidden" data-testid="staatid-alder-fordeling">
      <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('alder.title')}{scope ? ` — ${scope}` : ''}
        </span>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {utenAlder > 0
            ? t('alder.introUtenAlder', { total: total.toLocaleString('nb-NO'), uten: utenAlder.toLocaleString('nb-NO') })
            : t('alder.intro', { total: total.toLocaleString('nb-NO') })}
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('alder.bilalder')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.antall')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.medianDager')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.snittDager')}</th>
            <th className="px-4 py-2 w-1/3" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.label}
              data-testid={`staatid-alder-rad-${i}`}
              className={`border-b border-gray-100 dark:border-dark-border ${i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
            >
              <td className="px-4 py-2">{row.label}</td>
              <td className="px-4 py-2 text-right tabular-nums">{row.antall.toLocaleString('nb-NO')}</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.median_dager)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.snitt_dager)}</td>
              <td className="px-4 py-2">
                <div className="h-2 rounded-sm bg-gray-100 dark:bg-dark-border overflow-hidden">
                  <div
                    className="h-full bg-primary-500"
                    style={{ width: `${((row.median_dager ?? 0) / maxMedian) * 100}%` }}
                  />
                </div>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">{t('common.noData')}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
